import { Injectable } from '@angular/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

type ThemeMode = 'light' | 'dark';

/**
 * Native theming. The app ships a first-class light + dark theme driven by
 * CSS custom properties (see styles.scss). Dark mode is activated by setting
 * `data-theme="dark"` (app + Material + PrimeNG tokens) and `data-bs-theme="dark"`
 * (Bootstrap) on the <html> element. This replaces the former Dark Reader
 * runtime-inversion dependency.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemingService {
  private readonly STORAGE_KEY = 'actively-theme';
  private current: ThemeMode = 'light';

  constructor() {
    const saved = this.readSaved();
    const prefersDark =
      window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
    this.apply(saved ?? (prefersDark ? 'dark' : 'light'), false);

    // Follow the OS only until the user makes an explicit choice.
    window
      .matchMedia?.('(prefers-color-scheme: dark)')
      .addEventListener('change', (e) => {
        if (!this.readSaved()) this.apply(e.matches ? 'dark' : 'light', false);
      });
  }

  isDark(): boolean {
    return this.current === 'dark';
  }

  /** Flip + persist the user's explicit choice. Bound to the drawer toggle. */
  toggleTheme(): void {
    this.apply(this.current === 'dark' ? 'light' : 'dark', true);
  }

  setTheme(mode: ThemeMode): void {
    this.apply(mode, true);
  }

  private apply(mode: ThemeMode, persist: boolean): void {
    this.current = mode;
    const root = document.documentElement;
    if (mode === 'dark') {
      root.setAttribute('data-theme', 'dark');
      root.setAttribute('data-bs-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
      root.removeAttribute('data-bs-theme');
    }
    if (persist) {
      try {
        localStorage.setItem(this.STORAGE_KEY, mode);
      } catch {
        /* storage unavailable — keep the in-memory choice */
      }
    }
    this.syncThemeColorMeta(mode);
    this.syncStatusBar(mode);
  }

  private readSaved(): ThemeMode | null {
    try {
      const v = localStorage.getItem(this.STORAGE_KEY);
      return v === 'dark' || v === 'light' ? v : null;
    } catch {
      return null;
    }
  }

  private syncThemeColorMeta(mode: ThemeMode): void {
    let meta = document.querySelector(
      'meta[name="theme-color"]'
    ) as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = mode === 'dark' ? '#0b0f17' : '#ffffff';
  }

  private async syncStatusBar(mode: ThemeMode): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      // Style.Dark = light icons (for a dark bar); Style.Light = dark icons.
      await StatusBar.setStyle({
        style: mode === 'dark' ? Style.Dark : Style.Light,
      });
    } catch (err) {
      console.error('StatusBar error:', err);
    }
  }
}
